import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { STAGE_MAP, HEALTH_STYLE } from "@/lib/constants";
import { Progress } from "@/components/ui/progress";
import { Rocket, Loader2, CheckCircle2, CircleDashed } from "lucide-react";

export default function Readiness() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { api.get("/readiness").then((r) => setData(r.data)); }, []);

  if (!data) return <div className="flex justify-center py-40"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="p-6">
      <header className="mb-6">
        <h1 className="font-heading text-4xl font-black tracking-tighter flex items-center gap-3">
          <Rocket className="w-8 h-8 text-primary" /> Client Demo Readiness
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Every product with its pipeline progress and demo-ready status. This is the "are we ready to show the client?" board.</p>
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
        <Kpi label="Total Products" value={data.total} tone="muted" />
        <Kpi label="Demo Ready" value={data.ready} tone="success" />
        <Kpi label="Not Ready" value={data.total - data.ready} tone="warning" />
      </div>

      <div className="border border-border rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-card border-b border-border text-left">
              {["Product", "Current Phase", "Owner", "Pipeline", "Demo Prep", "Status"].map((h) => (
                <th key={h} className="p-2.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.products.map((p) => {
              const health = HEALTH_STYLE[p.health] || HEALTH_STYLE.idle;
              return (
                <tr key={p.id} data-testid={`readiness-row-${p.id}`} onClick={() => navigate(`/ideas/${p.id}`)}
                  className="border-b border-border hover:bg-white/[0.04] cursor-pointer transition-colors duration-200">
                  <td className="p-2.5 font-medium">{p.title}</td>
                  <td className="p-2.5 text-muted-foreground">{STAGE_MAP[p.current_stage]?.short}</td>
                  <td className="p-2.5 text-muted-foreground text-xs">{p.current_owner || <span className="text-danger">Unassigned</span>}</td>
                  <td className="p-2.5 w-40">
                    <div className="flex items-center gap-2">
                      <Progress value={p.pipeline_pct} className="h-1.5 flex-1" />
                      <span className="font-mono text-[10px] text-muted-foreground w-8">{p.pipeline_pct}%</span>
                    </div>
                  </td>
                  <td className="p-2.5 w-40">
                    <div className="flex items-center gap-2">
                      <Progress value={p.demo_pct} className="h-1.5 flex-1" />
                      <span className="font-mono text-[10px] text-muted-foreground w-8">{p.demo_pct}%</span>
                    </div>
                  </td>
                  <td className="p-2.5">
                    {p.demo_ready ? (
                      <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 border rounded-sm text-success border-success/40 bg-success/10">
                        <CheckCircle2 className="w-3 h-3" /> Demo Ready
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 border rounded-sm text-muted-foreground border-border">
                        <CircleDashed className="w-3 h-3" /> In Prep
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
            {data.products.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">No products yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Kpi({ label, value, tone }) {
  const color = { success: "#22C55E", warning: "#F59E0B", muted: "#fff" }[tone];
  return (
    <div className="border border-border bg-card rounded-sm p-4">
      <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
      <p className="font-heading text-3xl font-black mt-1" style={{ color }}>{value}</p>
    </div>
  );
}
